import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, History, CheckCircle2, Clock, AlertTriangle, FileText, Sparkles } from 'lucide-react';
import { matApi } from '../../api/materialthequeApi';

/**
 * MaterialthequeHistoryDropdown — bouton + menu déroulant listant toutes les
 * analyses Matériauthèque liées à un projet. Permet de basculer entre versions.
 *
 * Props:
 *  - projectId (string)
 *  - currentJobId (string|null) — le job actuellement affiché (pour le marquer "actif")
 */
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const statusIcon = (job) => {
  if (job.approved_at) return { Icon: CheckCircle2, color: '#86EFAC', label: 'Validée' };
  if (job.status === 'error') return { Icon: AlertTriangle, color: '#FCA5A5', label: 'Erreur' };
  if (['queued', 'parsing', 'extracting', 'thumbs'].includes(job.status)) return { Icon: Clock, color: '#93C5FD', label: 'En cours' };
  if (job.status === 'done') return { Icon: Sparkles, color: 'var(--c-gold)', label: 'À valider' };
  return { Icon: FileText, color: 'var(--c-text-muted)', label: job.status || '?' };
};

const MaterialthequeHistoryDropdown = ({ projectId, currentJobId }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    matApi.listProjectJobs(projectId)
      .then((r) => setJobs(r?.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [projectId, currentJobId]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!projectId) return null;
  if (!loading && jobs.length === 0) return null; // Pas d'historique = pas de bouton
  if (!loading && jobs.length === 1) return null; // 1 seul job = pas la peine

  const switchTo = (jobId) => {
    setOpen(false);
    navigate(`/materialtheque?project=${projectId}&job=${jobId}`);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="materialtheque-history-dropdown">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="hud-btn hud-btn-ghost hud-btn-sm"
        data-testid="materialtheque-history-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <History className="w-3.5 h-3.5" />
        <span>Historique ({jobs.length})</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-[340px] max-h-[420px] overflow-y-auto rounded-xl shadow-xl z-50"
          style={{
            background: 'var(--c-surface-1)',
            border: '1px solid var(--c-border)',
            boxShadow: 'var(--shadow-cockpit, 0 12px 40px rgba(0,0,0,0.6))',
          }}
          data-testid="materialtheque-history-menu"
        >
          <div className="px-3 py-2 sticky top-0" style={{ background: 'var(--c-surface-1)', borderBottom: '1px solid var(--c-border)' }}>
            <span className="hud-eyebrow" style={{ color: 'var(--c-text-muted)' }}>
              {jobs.length} analyse{jobs.length > 1 ? 's' : ''} pour ce projet
            </span>
          </div>
          <ul className="py-1">
            {jobs.map((j) => {
              const isCurrent = j.job_id === currentJobId;
              const { Icon, color, label } = statusIcon(j);
              return (
                <li key={j.job_id}>
                  <button
                    type="button"
                    onClick={() => switchTo(j.job_id)}
                    className="w-full text-left px-3 py-2 transition-colors hover:bg-white/5 flex items-start gap-2.5"
                    style={isCurrent ? { background: 'rgba(217,160,91,0.06)' } : {}}
                    data-testid={`materialtheque-history-item-${j.job_id}`}
                    role="menuitem"
                  >
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium truncate" style={{ color: 'var(--c-text)' }}>
                          {j.file_name || 'CCTP'}
                        </span>
                        {isCurrent && (
                          <span className="hud-eyebrow hud-eyebrow-gold" style={{ fontSize: 10 }}>· actif</span>
                        )}
                      </div>
                      <div className="text-[11.5px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: 'var(--c-text-muted)' }}>
                        <span style={{ color }}>{label}</span>
                        <span>·</span>
                        <span>{j.materials_count || 0} matériaux</span>
                        <span>·</span>
                        <span>{formatDate(j.created_at)}</span>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MaterialthequeHistoryDropdown;
